import {
  Loader2,
  RefreshCwIcon,
  TriangleAlertIcon,
} from "lucide-react"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { AppBreadcrumb } from "@/components/layout/app-breadcrumb"
import { Header } from "@/components/layout/header"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { FRONTEND_ROUTES } from "@/constants"
import { formatDate, formatMoney } from "@/helpers/format"
import {
  useReprocessAllMutation,
  useReprocessSelectedMutation,
  useWaitingWithPaidTransaction,
} from "@/services/admin"
import type { WaitingPaidPaymentMethod } from "@/types/admin-reprocess"

import {
  areAllSelected,
  clearSelection,
  selectAll,
  toggleSelection,
} from "./selection"
import { resolveTableBodyState } from "./table-state"

// Paginação fixa da listagem de casos presos.
const PAGE = 0
const PAGE_SIZE = 50
// Placeholder para valores nulos.
const EM_DASH = "—"
// Nº total de colunas (checkbox + 9 dados) — usado em colSpan.
const COLUMN_COUNT = 10

type ConfirmMode = "selected" | "all" | null

// Formata valor em centavos como moeda BRL; null vira "—".
const formatAmountCell = (amount: number | null): string =>
  amount === null ? EM_DASH : formatMoney(amount / 100)

// Formata data ISO de forma legível; null/invalida vira "—".
const formatDateCell = (value: string | null): string => {
  if (!value) return EM_DASH
  try {
    return formatDate({
      date: value,
      formatLayout: "dd/MM/yyyy HH:mm",
    })
  } catch {
    return value
  }
}

// Resolve a chave i18n do método de pagamento (ou null p/ fallback).
const resolveMethodKey = (
  method: WaitingPaidPaymentMethod | null
): string | null => {
  if (method === "BANK_SLIP")
    return "pages.reprocessPayments.methodBankSlip"
  if (method === "PIX")
    return "pages.reprocessPayments.methodPix"
  return null
}

export function ReprocessPaymentsPage() {
  const { t } = useTranslation()

  const { data, isLoading, isError, isFetching, refetch } =
    useWaitingWithPaidTransaction(PAGE, PAGE_SIZE)

  const reprocessSelectedMutation =
    useReprocessSelectedMutation()
  const reprocessAllMutation = useReprocessAllMutation()

  const [selected, setSelected] = useState<Set<string>>(
    () => clearSelection()
  )
  const [confirmMode, setConfirmMode] =
    useState<ConfirmMode>(null)

  const payments = useMemo(
    () => data?.payments ?? [],
    [data?.payments]
  )
  const orderIds = useMemo(
    () => payments.map((payment) => payment.orderId),
    [payments]
  )

  const allSelected = areAllSelected(orderIds, selected)
  const hasSelection = selected.size > 0

  // O que o corpo da tabela renderiza: skeleton (carregamento inicial OU
  // refetch, ex.: botão "Atualizar"), linhas de dados ou empty state —
  // nunca empty state enquanto há carregamento em andamento.
  const tableBodyState = resolveTableBodyState(
    isLoading,
    isFetching,
    payments.length
  )

  const isMutating =
    reprocessSelectedMutation.isPending ||
    reprocessAllMutation.isPending

  const handleToggleAll = () => {
    setSelected((prev) =>
      areAllSelected(orderIds, prev)
        ? clearSelection()
        : selectAll(orderIds)
    )
  }

  const handleToggleRow = (orderId: string) => {
    setSelected((prev) => toggleSelection(prev, orderId))
  }

  // Abre o modal de "Reprocessar todos" já disparando um refetch
  // para exibir a contagem global atualizada.
  const handleOpenReprocessAll = () => {
    refetch()
    setConfirmMode("all")
  }

  const handleConfirm = async () => {
    if (confirmMode === "selected") {
      await reprocessSelectedMutation.mutateAsync(
        Array.from(selected)
      )
      setSelected(clearSelection())
    } else if (confirmMode === "all") {
      await reprocessAllMutation.mutateAsync()
      setSelected(clearSelection())
    }
    setConfirmMode(null)
  }

  return (
    <main className="flex w-full flex-1 flex-col">
      <Header>
        <AppBreadcrumb
          links={[
            {
              href: FRONTEND_ROUTES.DASHBOARD.href,
              label: t("sidebar.dashboard"),
            },
            {
              label: t("sidebar.administration"),
            },
            {
              label: t("sidebar.reprocessPayments"),
            },
          ]}
        />
      </Header>
      <section className="flex flex-1 flex-col gap-4 overflow-x-hidden p-4 @container">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-bold tracking-tight">
            {t("pages.reprocessPayments.title")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("pages.reprocessPayments.subtitle")}
          </p>
        </div>

        <Card>
          <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle>
              {t("pages.reprocessPayments.casesFound")}
              {data ? ` (${data.total})` : ""}
            </CardTitle>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                type="button"
                variant="outline"
                disabled={isFetching || isMutating}
                onClick={() => refetch()}
              >
                <RefreshCwIcon
                  className={`mr-2 h-4 w-4 ${isFetching ? "animate-spin" : ""}`}
                />
                {t("pages.reprocessPayments.refresh")}
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={
                  !hasSelection || isMutating || isLoading
                }
                onClick={() => setConfirmMode("selected")}
              >
                {reprocessSelectedMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {t(
                  "pages.reprocessPayments.reprocessSelected"
                )}
              </Button>
              <Button
                type="button"
                disabled={
                  isMutating ||
                  isLoading ||
                  payments.length === 0
                }
                onClick={handleOpenReprocessAll}
              >
                {reprocessAllMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCwIcon className="mr-2 h-4 w-4" />
                )}
                {t("pages.reprocessPayments.reprocessAll")}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto rounded-md border">
              <Table className="min-w-[1100px]">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[44px]">
                      <Checkbox
                        aria-label={t(
                          "pages.reprocessPayments.selectAll"
                        )}
                        checked={allSelected}
                        disabled={
                          isLoading || payments.length === 0
                        }
                        onCheckedChange={handleToggleAll}
                      />
                    </TableHead>
                    <TableHead>
                      {t(
                        "pages.reprocessPayments.columnOrderId"
                      )}
                    </TableHead>
                    <TableHead>
                      {t(
                        "pages.reprocessPayments.columnPaymentUuid"
                      )}
                    </TableHead>
                    <TableHead>
                      {t(
                        "pages.reprocessPayments.columnStatus"
                      )}
                    </TableHead>
                    <TableHead>
                      {t(
                        "pages.reprocessPayments.columnWhitelabel"
                      )}
                    </TableHead>
                    <TableHead className="text-right">
                      {t(
                        "pages.reprocessPayments.columnAmount"
                      )}
                    </TableHead>
                    <TableHead>
                      {t(
                        "pages.reprocessPayments.columnPaymentMethod"
                      )}
                    </TableHead>
                    <TableHead>
                      {t(
                        "pages.reprocessPayments.columnCreatedAt"
                      )}
                    </TableHead>
                    <TableHead>
                      {t(
                        "pages.reprocessPayments.columnPaidAt"
                      )}
                    </TableHead>
                    <TableHead>
                      {t(
                        "pages.reprocessPayments.columnUpdatedAt"
                      )}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tableBodyState === "skeleton" &&
                    Array.from({ length: 5 }).map(
                      (_, rowIndex) => (
                        <TableRow
                          key={`skeleton-${rowIndex}`}
                        >
                          {Array.from({
                            length: COLUMN_COUNT,
                          }).map((__, cellIndex) => (
                            <TableCell
                              key={`skeleton-${rowIndex}-${cellIndex}`}
                            >
                              <Skeleton className="h-4 w-full" />
                            </TableCell>
                          ))}
                        </TableRow>
                      )
                    )}

                  {tableBodyState === "rows" &&
                    payments.map((payment) => (
                      <TableRow key={payment.orderId}>
                        <TableCell>
                          <Checkbox
                            aria-label={t(
                              "pages.reprocessPayments.selectOrder",
                              { orderId: payment.orderId }
                            )}
                            checked={selected.has(
                              payment.orderId
                            )}
                            onCheckedChange={() =>
                              handleToggleRow(
                                payment.orderId
                              )
                            }
                          />
                        </TableCell>
                        <TableCell className="font-medium tabular-nums">
                          {payment.orderId}
                        </TableCell>
                        <TableCell className="tabular-nums">
                          {payment.paymentUuid}
                        </TableCell>
                        <TableCell>
                          {payment.status}
                        </TableCell>
                        <TableCell>
                          {payment.whiteLabelId ?? EM_DASH}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatAmountCell(payment.amount)}
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const key = resolveMethodKey(
                              payment.paymentMethod
                            )
                            if (key) return t(key)
                            return (
                              payment.paymentMethod ??
                              EM_DASH
                            )
                          })()}
                        </TableCell>
                        <TableCell className="whitespace-nowrap tabular-nums">
                          {formatDateCell(
                            payment.createdAt
                          )}
                        </TableCell>
                        <TableCell className="whitespace-nowrap tabular-nums">
                          {formatDateCell(payment.paidAt)}
                        </TableCell>
                        <TableCell className="whitespace-nowrap tabular-nums">
                          {formatDateCell(
                            payment.updatedAt
                          )}
                        </TableCell>
                      </TableRow>
                    ))}

                  {tableBodyState === "empty" && (
                    <TableRow>
                      <TableCell
                        colSpan={COLUMN_COUNT}
                        className="py-10 text-center text-sm text-muted-foreground"
                      >
                        {isError
                          ? t(
                              "pages.reprocessPayments.loadError"
                            )
                          : t(
                              "pages.reprocessPayments.emptyState"
                            )}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </section>

      <AlertDialog
        open={confirmMode !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmMode(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("pages.reprocessPayments.confirmTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmMode === "selected"
                ? t(
                    "pages.reprocessPayments.confirmSelected",
                    { count: selected.size }
                  )
                : t("pages.reprocessPayments.confirmAll", {
                    total: isFetching
                      ? "…"
                      : (data?.total ?? "…"),
                  })}
            </AlertDialogDescription>
            <div className="flex items-start gap-2 rounded-md text-sm text-amber-600 dark:text-amber-500">
              <TriangleAlertIcon
                aria-hidden="true"
                className="mt-0.5 h-4 w-4 shrink-0"
              />
              <span>
                {t("pages.reprocessPayments.disclaimer")}
              </span>
            </div>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isMutating}>
              {t("pages.reprocessPayments.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={
                isMutating ||
                (confirmMode === "all" && isFetching)
              }
              onClick={(event) => {
                event.preventDefault()
                handleConfirm()
              }}
            >
              {isMutating && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {t("pages.reprocessPayments.execute")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  )
}
